import { Message } from '@weave/schema-gen-ts/dist/schemas/messaging/auto-worker/v1/models.pb';
import { MessageGroup } from '@weave/schema-gen-ts/dist/schemas/messaging/auto-worker/v1/service.pb';
import { OutboundMessageStatus } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/enums.pb';
import dayjs from 'dayjs';
import { useQueryClient } from 'react-query';
import { AutoWorkerMutations, AutoWorkerTypes } from '@frontend/api-auto-worker';
import { PersonTypes, PersonHelpers, PersonsV3 } from '@frontend/api-person';
import { getUser } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { useFormatStatuses } from '@frontend/message-queue-hooks';
import { getMessageStatusColors } from '@frontend/message-status-colors';
import { Photos } from '@frontend/photos';
import { kebabCase, sentenceCase } from '@frontend/string';
import { theme } from '@frontend/theme';
import { Avatar, Chip, NakedButton, phone, SecondaryButton, Text, useAlert, useTooltip } from '@frontend/design-system';
import { formatFutureTimestamp, formatMatchingDestinations, formatPastTimestamp } from './utils';

type Props = {
  avatarOnClick?: () => void;
  canSuppress?: boolean;
  cardType?: 'person' | 'rule';
  hideBadgeAndButton?: boolean;
  isNext?: boolean;
  message: Message;
  messageDetails?: string;
  person?: Partial<PersonTypes.Person>;
  queryKeyToInvalidate?: AutoWorkerTypes.ScopedMessageRequestQueryKey;
  showAvatar?: boolean;
  showContactInfo?: boolean;
  showTimestamp?: boolean;
  simplifiedTimestamp?: boolean;
  trackingPrefix: string;
};

export const AutoMessageSuppressionCard = ({
  avatarOnClick,
  canSuppress = false,
  cardType = 'rule',
  hideBadgeAndButton = false,
  isNext,
  message,
  messageDetails,
  person,
  queryKeyToInvalidate = [],
  showAvatar = false,
  showContactInfo = false,
  showTimestamp = false,
  simplifiedTimestamp = false,
  trackingPrefix,
  ...rest
}: Props) => {
  const { t } = useTranslation('messages');
  const queryClient = useQueryClient();
  const alert = useAlert();

  const { destination, locationId = '', messageId, ruleId, ruleName, sendAt, statuses, suppressedAt } = message;
  const { userID: userId = '' } = getUser() ?? {};
  const lastStatus = statuses?.at(-1);
  const currentStatus = lastStatus?.status || message.currentStatus || OutboundMessageStatus.UNSPECIFIED;
  const statusDetails = lastStatus?.details ?? [];
  const statusDetail = sentenceCase(statusDetails[0] ?? '');
  const { Tooltip, tooltipProps, triggerProps } = useTooltip({
    placement: 'top-end',
  });

  const contactName =
    PersonHelpers.getFullName({
      FirstName: person?.FirstName,
      LastName: person?.LastName,
      PreferredName: person?.PreferredName,
    }) || t('Unknown');

  const { data: formattedDestination } = PersonsV3.PersonQueries.useGetPersonLegacyQuery(
    {
      personId: person?.PersonID ?? '',
      locationIds: [locationId],
    },
    {
      enabled: !!person?.PersonID && !!locationId,
      select: (data) => formatMatchingDestinations(data?.contactInfo, destination),
    }
  );

  const handleError = (context: unknown, message: string) => {
    const previous = (context as { previous: Record<string, MessageGroup> }).previous;
    queryClient.setQueryData(queryKeyToInvalidate, previous);
    alert.error(message);
  };

  const { mutate: suppressMessage } = AutoWorkerMutations.useSuppressMessage(queryKeyToInvalidate, {
    onError: (_err, _req, context: unknown) =>
      handleError(context, t('There was an error skipping the message. Please try again later.')),
  });
  const { mutate: unsuppressMessage } = AutoWorkerMutations.useUnsuppressMessage(queryKeyToInvalidate, {
    onError: (_err, _req, context: unknown) =>
      handleError(context, t('There was an error resuming the message. Please try again later.')),
  });

  const { failedGroupedStatuses, formatDisplayedStatus, getGroupedStatus, successfulGroupedStatuses } =
    useFormatStatuses();
  const canSend = [OutboundMessageStatus.UNSPECIFIED, OutboundMessageStatus.SCHEDULED].includes(currentStatus);
  const hasSent = successfulGroupedStatuses.includes(getGroupedStatus(currentStatus));
  const willNotSend = failedGroupedStatuses.includes(getGroupedStatus(currentStatus));

  const checkCanChange = (time?: string) => {
    const WINDOW_TIME = 30; // cut off time defined by the backend in minutes
    const timestamp = dayjs(time);
    const now = dayjs();
    const isAfter = timestamp.isAfter(now);
    const withinWindowTime = now.add(WINDOW_TIME, 'minute');
    return isAfter && timestamp.isAfter(withinWindowTime);
  };

  const { backgroundColor, textColor: color } = getMessageStatusColors(currentStatus);
  const formattedCurrentStatus = formatDisplayedStatus(getGroupedStatus(currentStatus));
  const displayedCurrentStatus =
    currentStatus === OutboundMessageStatus.NOT_SENT
      ? isNext
        ? t('Not Sending')
        : t('Not Sent')
      : formattedCurrentStatus;

  const canChange = checkCanChange(sendAt);
  const now = dayjs();
  const isFuture = dayjs(sendAt).isAfter(now);

  return (
    <div
      className='auto-message-suppression-card'
      css={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(2),
        borderBottom: `1px solid ${theme.colors.neutral20}`,
        '&:last-of-type': { borderBottom: 'none' },
      }}
      {...rest}
    >
      <div css={{ display: 'flex', columnGap: theme.spacing(1.5), alignItems: 'center' }}>
        {showAvatar &&
          (person ? (
            <Photos.ContactProfilePhoto
              name={contactName}
              personId={person.PersonID!}
              locationId={locationId}
              onClick={avatarOnClick}
              data-trackingid={trackingPrefix + '-suppression-card-contact-profile-photo'}
            />
          ) : (
            <Avatar size='large' data-trackingid={trackingPrefix + '-suppression-card-avatar'} />
          ))}
        <div css={{ display: 'flex', flexDirection: 'column', rowGap: theme.spacing(0.5) }}>
          <Text
            weight='bold'
            css={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {cardType === 'person' ? contactName : ruleName}
          </Text>
          {showContactInfo && <Text size='small'>{formattedDestination ?? phone(destination ?? '')}</Text>}
          {messageDetails && <Text size='small'>{messageDetails}</Text>}
          {showTimestamp && (
            <Text size='small' color='light'>
              {isFuture
                ? formatFutureTimestamp({
                    simplified: simplifiedTimestamp,
                    time: sendAt,
                    willNotSend,
                  })
                : formatPastTimestamp({
                    hasSent,
                    time: sendAt,
                  })}
            </Text>
          )}
        </div>
      </div>
      {!hideBadgeAndButton &&
        (canSuppress && canSend ? (
          suppressedAt ? (
            <NakedButton
              disabled={!canChange || !locationId || !messageId}
              onClick={() => locationId && messageId && unsuppressMessage({ locationId, messageId })}
              css={{ cursor: !canChange || !locationId || !messageId ? 'default' : 'pointer' }}
              trackingId={trackingPrefix + '-suppression-card-resume-button'}
            >
              <Text color={!canChange ? 'disabled' : 'primary'} weight='bold'>
                {t('Resume')}
              </Text>
            </NakedButton>
          ) : (
            <SecondaryButton
              size='tiny'
              disabled={!canChange || !locationId || !messageId || !ruleId || !userId}
              onClick={() =>
                locationId &&
                messageId &&
                ruleId &&
                userId &&
                suppressMessage({ locationId, messageId, ruleId, userId })
              }
              trackingId={trackingPrefix + '-suppression-card-skip-button'}
            >
              {t('Skip')}
            </SecondaryButton>
          )
        ) : (
          <>
            <Chip
              css={{ backgroundColor, maxWidth: theme.spacing(12), color }}
              hideTooltip
              {...triggerProps}
              trackingId={trackingPrefix + '-suppression-card-' + kebabCase(displayedCurrentStatus) + '-status'}
            >
              {t('{{status}}', { status: displayedCurrentStatus })}
            </Chip>
            <Tooltip {...tooltipProps} css={{ maxWidth: 'unset' }}>
              <Text size='small' color='white' weight='regular'>
                {statusDetail}
              </Text>
            </Tooltip>
          </>
        ))}
    </div>
  );
};
